import React from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery } from '@apollo/client'
import { DEFAULT_CURRENCY_CODE } from 'lib/core/resources/constants'
import { Price } from 'lib/shared/components'
import { Spinner } from 'lib/ui/components'
import { useCart } from 'lib/core/hooks'
import { getCartSlice } from 'lib/core/data/quote/cart/selectors'
import { getStoreConfig } from 'services/storeConfig'
import { useCartTotals, SubscriptionItem } from 'hooks/cart'
import { TotalElement, Title } from './components/Styled'
import { CartData, CartSlice } from 'lib/core/data/quote/cart/types'

interface Props {
  abandonedCart?: {
    isFirstLoad: boolean
    isFetching: boolean
    isSuccess: boolean
    isFailure: boolean
    isCartException: boolean
    cartId: string
    count: number
    data?: CartData
  }
  cartRaw?: CartSlice
}

export const TotalsCart = ({ abandonedCart, cartRaw }: Props) => {
  const { cart: realCart } = useCart()
  const cart = abandonedCart ?? realCart
  const { pathname } = useLocation()
  const completeCartSelector = useSelector(getCartSlice(cart?.cartId ?? ''))
  const completeCart = cartRaw ?? completeCartSelector
  const { loading, data } = useQuery(getStoreConfig)
  const shippingFlatRate = data?.storeConfig?.carriers_flatrate_price
  const minimunSubtotalForFreeShipping = data?.storeConfig?.carriers_freeshipping_free_shipping_subtotal
  const isCheckout = pathname !== '/cart' && !abandonedCart

  const {
    subscriptionItemsInCart,
    subscriptionItemsPricesByFrequency
  } = useCartTotals(cart?.data?.items)

  if (!cart?.data) {
    return null
  }

  const subtotalWithDiscount = completeCart?.data?.prices.subtotal_with_discount_excluding_tax

  return (
    <>
      {isCheckout && subscriptionItemsInCart?.length > 0 && (
        <Title>
          <strong>Recurring Charges</strong>
        </Title>
      )}
      {!isCheckout && (
        <TotalElement>
          <p className='label'>
            Charged today
          </p>
          <h2 className='price'>
            {subtotalWithDiscount && (
              <Price
                {...subtotalWithDiscount}
              />
            )}
          </h2>
          <p>
            Taxes and shipping calculated at checkout.
          </p>
          <p>
            Ready to ship within 1-3 days
          </p>
        </TotalElement>
      )}
      {subscriptionItemsInCart?.length > 0 && (
        <TotalElement>
          {subscriptionItemsPricesByFrequency?.map((item: SubscriptionItem, i) => {
            const { frequencyInCart, itemTotal, productName } = item
            const frenquencyInWeeks = parseInt(frequencyInCart) / 7
            const names = productName?.split(',')
            const hasFreeShipping = itemTotal >= minimunSubtotalForFreeShipping
            return (
              <div key={i}>
                <p className='label'>
                  Charged every {frenquencyInWeeks} weeks
                </p>
                {names.map((name, i) => (
                  <span key={i}>{name}</span>
                ))}
                <h2 className='price'>
                  <Price
                    value={itemTotal}
                    currency={DEFAULT_CURRENCY_CODE}
                  />
                </h2>
                {loading ? (
                  <Spinner />
                ) : (
                  <p>
                    {hasFreeShipping ? (
                      <span>
                        Taxes calculated at time of shipment.
                        <br />
                        Your order qualifies for free shipping!
                      </span>
                    ) : (
                      <span>
                        Taxes and <Price value={shippingFlatRate} currency={DEFAULT_CURRENCY_CODE} /> shipping charge added at time of shipment.
                      </span>
                    )}
                  </p>
                )}
              </div>
            )
          })}
        </TotalElement>
      )}
    </>
  )
}
